'use client'

import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Shield, Lock, Server, FileKey, Users, Database, CheckCircle2 } from 'lucide-react'

export default function SecurityPage() {
  const securityFeatures = [
    {
      icon: Shield,
      title: 'Enterprise-Grade Security',
      description: 'Our infrastructure is built on AWS with multiple layers of security controls.',
    },
    {
      icon: Lock,
      title: 'Data Encryption',
      description: 'All data is encrypted at rest and in transit using industry-standard protocols.',
    },
    {
      icon: Server,
      title: 'Isolated Infrastructure',
      description: 'Dedicated environments ensure complete separation of customer data.',
    },
    {
      icon: Database,
      title: 'Regular Backups',
      description: 'Automated backups with point-in-time recovery capabilities.',
    },
  ]

  const complianceStandardsData = [
    { title: 'SOC 2 Type II' },
    { title: 'GDPR Compliant' },
    { title: 'NIST 800-53' },
    { title: 'ISO 27001' },
    { title: 'FedRAMP Ready' },
  ]

  const securityPractices = [
    'Continuous monitoring and threat detection',
    'Incident response and disaster recovery plans',
    'Vendor security assessment program',
    'Regular security patches and updates',
    'Data retention and disposal policies',
    'Physical security measures',
  ]

  return (
    <div className="container max-w-7xl mx-auto px-4 py-16">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <h1 className="text-5xl font-bold mb-6">Security First, Always</h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          At GovCon Data, security is not just a feature - it's the foundation of everything we do.
          We implement the highest standards of security to protect your sensitive data.
        </p>
      </div>

      {/* Security Features Grid */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold text-center mb-12">Security Infrastructure</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {securityFeatures.map((feature) => (
            <div 
              key={feature.title} 
              className="border rounded-lg p-6 bg-card text-center transition-transform duration-200 hover:scale-105 hover:shadow-lg"
            >
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-lg bg-primary/10">
                  <feature.icon className="h-8 w-8 text-primary" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
              <p className="text-muted-foreground">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Security Practices */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold text-center mb-12">Security Practices</h2>
        <div className="grid md:grid-cols-2 gap-8">
          {securityPractices.map((practice) => (
            <div key={practice} className="flex items-start gap-3">
              <div className="mt-1">
                <CheckCircle2 className="h-5 w-5 text-primary" />
              </div>
              <p className="text-lg">{practice}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Simplified Compliance Standards Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold text-center mb-12">Compliance Standards</h2>
        <div className="flex flex-wrap justify-center gap-4">
          {complianceStandardsData.map((standard) => (
            <span key={standard.title} className="inline-flex items-center rounded-full bg-muted px-4 py-1.5 text-sm font-medium text-muted-foreground shadow-sm">
              {standard.title}
            </span>
          ))}
        </div>
      </div>

      {/* Updated Contact Section */}
      <div className="text-center mt-20">
        <h2 className="text-3xl font-bold mb-4">Questions About Security?</h2>
        <p className="text-lg text-muted-foreground mb-8">
          Our team is ready to answer your questions about our security measures.
        </p>
        <Link href="/contact" passHref>
          <Button>Contact Us</Button>
        </Link>
      </div>
    </div>
  )
} 